import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import {
  Box,
  Link as MuiLink,
  Snackbar,
  Stack,
  Typography,
} from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import { forgotPasswordSendOtp, forgotPasswordValidateOtp, forgotPassword } from "../../action/password";
import { validatePassword } from "../../utils/general";
import Dialog from "../Common/Dailog/Dailog";
import FormInput from "../Common/FormInputs/MuiTextField";
import AppButton from "../Common/Button/Button";

function Alert(props) {
  return <MuiAlert elevation={6} variant="filled" {...props} />;
}

const ForgotPassword = ({
  open,
  onClose,
  forgotPasswordSendOtp,
  forgotPasswordValidateOtp,
  forgotPassword,
}) => {
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [token, setToken] = useState("");
  const [counter, setCounter] = useState(30);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("error");

  const showToast = (message, severity = "error") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbarOpen(false);
  };

  useEffect(() => {
    if (step === 2) setCounter(30);
  }, [step]);

  useEffect(() => {
    if (step !== 2 || counter <= 0) return;

    const timerId = setInterval(() => {
      setCounter((c) => c - 1);
    }, 1000);
    return () => clearInterval(timerId);
  }, [step, counter]);

  const handleReset = () => {
    setStep(1);
    setEmail("");
    setOtp("");
    setError("");
    setNewPassword("");
    setConfirmPassword("");
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  const sendOtp = () => {
    setError("");
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!email) {
      setError("Please enter your email address.");
      return;
    }

    if(!emailRegex.test(email)){
      setError("Please enter a valid email address.")
      return;
    }

    forgotPasswordSendOtp(email)
      .then((res) => {
        if (res?.status === 200) {
          setError("");
          setStep(2);
        } else {
          showToast("Unable to send OTP. Please try again.");
        }
      })
      .catch((err) => {
        const status = err?.response?.status;
        if (status === 500 || status === 429) {
          showToast(err?.response?.data?.message || err?.userMessage || "Failed to send OTP.", "danger");
        } else {
          showToast(err?.userMessage || "Failed to send OTP.");
        }
      });
  };

  const verifyOtp = () => {
    setError("");
    if (!otp) {
      setError("Please enter OTP.");
      return;
    }
    if(otp.length!==6){
      setError("Please enter valid otp.")
      return;
    }

    forgotPasswordValidateOtp(email, otp)
      .then((res) => {
        if (res?.status === 200) {
          setToken(res?.data?.token);
          setError("");
          setStep(3);
        } else {
          showToast("Unable to validate OTP. Please try again.");
        }
      })
      .catch((err) => {
        if (err?.response?.status === 500) {
          showToast(err?.response?.data?.message || err?.userMessage || "OTP verification failed.", "danger");
        } else {
          showToast(err?.userMessage || "OTP verification failed.");
        }
      });
  };

  const resetPassword = () => {
    setError("");

    if (!newPassword || !confirmPassword) {
      setError("Please fill both fields.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    const { valid, error: validationError } = validatePassword(newPassword);
    if (!valid) {
      setError(validationError);
      return;
    }

    forgotPassword(email, newPassword, token)
      .then((res) => {
        if (res?.status === 200) {
          setError("");
          setSuccess("Your password has been reset successfully. Please sign in again.");
          showToast("Password reset successful!", "success");
          setNewPassword("");
          setConfirmPassword("");
          onClose();
        } else {
          showToast("Unable to reset password. Please try again.");
        }
      })
      .catch((err) => {
        if (err?.response?.status === 500) {
          showToast(err?.response?.data?.message || err?.userMessage || "Password reset failed.", "danger");
        } else {
          showToast(err?.userMessage || "Password reset failed.");
        }
      });
  };

  const goBackToStep1 = () => {
    setStep(1);
    setOtp("");
    setNewPassword("");
    setConfirmPassword("");
    setError("");
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      title={step === 1 ? "Reset Password" : step === 2 ? "Enter OTP" : "Set New Password"}
      maxWidth="xs"
      fullWidth
      closeOnBackdropClick
      closeOnEscapeKeyDown
      contentSx={{ overflow: "visible", pt: 2, pb: 2 }}
    >
      <Box sx={{ width: "100%" }}>
        <Stack spacing={2}>
          {step === 1 && (
            <>
              <Typography variant="body2">
                Enter your registered email address to receive an OTP.
              </Typography>
              <FormInput
                label="Email Address"
                type="email"
                fullWidth
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoFocus
                error={Boolean(error)}
                helperText={error || ""}
                touched="true"
              />
              <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5, pt: 0.5 }}>
                <AppButton variant="ghost" onClick={handleClose}>
                  Cancel
                </AppButton>
                <AppButton variant="primary" onClick={sendOtp}>
                  Continue
                </AppButton>
              </Box>
            </>
          )}

          {step === 2 && (
            <>
              <Typography variant="body2">
                We sent an OTP to <strong>{email}</strong>
              </Typography>
              <FormInput
                label="OTP"
                type="text"
                fullWidth
                value={otp}
                onChange={(e) => {
                  if (/^[a-zA-Z0-9]{0,6}$/.test(e.target.value)) {
                    setOtp(e.target.value);
                  }
              
                }}
                autoFocus
                error={Boolean(error)}
                helperText={error || ""}
                touched="true"
              />
              <Box>
                {counter > 0 ? (
                  <Typography variant="caption" color="textSecondary">
                    Resend OTP in {counter}s
                  </Typography>
                ) : (
                  <Typography variant="caption">
                    Didn't receive it?{" "}
                    <MuiLink
                      component="button"
                      type="button"
                      onClick={() => {
                        sendOtp();
                        setOtp("");
                        setError("");
                        setCounter(30);
                      }}
                      underline="always"
                      sx={{ color: "primary.main", cursor: "pointer", background: "transparent", border: 0, p: 0 }}
                    >
                      Resend OTP
                    </MuiLink>
                  </Typography>
                )}
              </Box>
              <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5, pt: 0.5 }}>
                <AppButton variant="ghost" onClick={goBackToStep1}>
                  Back
                </AppButton>
                <AppButton variant="primary" onClick={verifyOtp}>
                  Continue
                </AppButton>
              </Box>
            </>
          )}

          {step === 3 && (
            <>
              <FormInput
                label="New Password"
                type="password"
                fullWidth
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                autoFocus
                error={Boolean(error)}
                helperText={error || ""}
                touched="true"
              />
              <FormInput
                label="Confirm Password"
                type="password"
                fullWidth
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                error={Boolean(error)}
                helperText={error || ""}
                touched="true"
              />
              {success && (
                <Typography color="primary" variant="body2">
                  {success}
                </Typography>
              )}
              <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5, pt: 0.5 }}>
                <AppButton variant="ghost" onClick={goBackToStep1}>
                  Back
                </AppButton>
                <AppButton variant="primary" onClick={resetPassword}>
                  Submit
                </AppButton>
              </Box>
            </>
          )}
        </Stack>
      </Box>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        slots={{
          transition: (props) => <div {...props} />,
        }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

const mapDispatchToProps = (dispatch) => ({
  forgotPasswordSendOtp: (email) => dispatch(forgotPasswordSendOtp(email)),
  forgotPasswordValidateOtp: (email, otp) => dispatch(forgotPasswordValidateOtp(email, otp)),
  forgotPassword: (email, newPassword, token) =>
    dispatch(forgotPassword(email, newPassword, token)),
});

export default connect(null, mapDispatchToProps)(ForgotPassword);
